import React, { useState } from 'react'
import { Button, CircularProgress, Tooltip } from '@mui/material'
import BookmarkAddIcon  from '@mui/icons-material/BookmarkAddOutlined'
import BookmarkAddedIcon from '@mui/icons-material/BookmarkAdded'
import { supabase } from '../../supabaseClient'

interface Props {
  projectId:   string
  stepNumber:  number
  sourceLabel: string                // e.g. "AI Coach Q&A — Honeybee health"
  content:     string                // the snippet to persist (Q+A combined, evaluation note, etc.)
  /** Optional compact rendering (icon-only, no label) */
  compact?:    boolean
}

/**
 * "Save to journal" — student-curated capture of an AI exchange or insight.
 * The system never auto-saves; the student decides what mattered. Once
 * clicked, the button shows a saved state with a 3s "Saved!" confirmation.
 */
export default function SaveToJournalButton({
  projectId, stepNumber, sourceLabel, content, compact,
}: Props) {
  const [saving, setSaving] = useState(false)
  const [saved,  setSaved]  = useState(false)
  const [error,  setError]  = useState<string | null>(null)

  async function handleSave() {
    if (saving || saved) return
    setSaving(true)
    setError(null)
    const { data, error: invokeErr } = await supabase.functions.invoke('save-journal-note', {
      body: {
        project_id:   projectId,
        step_number:  stepNumber,
        source_label: sourceLabel,
        content,
      },
    })
    setSaving(false)
    if (invokeErr || data?.error) {
      setError(invokeErr?.message ?? data?.error ?? 'Could not save')
      return
    }
    setSaved(true)
  }

  if (saved) {
    return (
      <Tooltip title="Saved to your journal">
        <Button
          size="small" variant="text"
          startIcon={<BookmarkAddedIcon fontSize="small" />}
          disabled
          sx={{ color: 'success.main', textTransform: 'none' }}
        >
          {compact ? '' : 'Saved'}
        </Button>
      </Tooltip>
    )
  }

  return (
    <Tooltip title={error ?? 'Save this to your research journal'}>
      <span>
        <Button
          size="small" variant="text"
          startIcon={saving ? <CircularProgress size={14} /> : <BookmarkAddIcon fontSize="small" />}
          onClick={handleSave}
          disabled={saving}
          sx={{ color: error ? 'error.main' : 'text.secondary', textTransform: 'none', fontSize: '0.78rem' }}
        >
          {compact ? '' : (error ? 'Retry' : 'Save to journal')}
        </Button>
      </span>
    </Tooltip>
  )
}
